import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { getNewLookTokens } from "common/NewLook/tokens.ts";

/** Overlays (drawers, modals, menus) scroll on their own; ignore them. */
const OVERLAY_SELECTOR =
  ".ant-drawer, .ant-modal-root, .ant-modal-wrap, .ant-popover, .ant-dropdown, .ant-select-dropdown, .ant-picker-dropdown";

interface ShadowRect {
  top: number;
  left: number;
  width: number;
}

/**
 * New look, phone: the header, tabs and filter bar sit still above the page's
 * own scroller, so content slides under them with nothing to separate the
 * two. Once the page is scrolled, a soft shadow fades in along the top edge
 * of the scrolling content, i.e. under the lowest bar of the chrome (header,
 * tabs or filters, whichever is last), whatever the page's structure.
 */
const PhoneChromeShadow = () => {
  const [rect, setRect] = useState<ShadowRect | null>(null);
  const [visible, setVisible] = useState(false);
  const activeRef = useRef<HTMLElement | null>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    activeRef.current = null;
    setVisible(false);
  }, [pathname]);

  useEffect(() => {
    const evaluate = () => {
      const scroller = activeRef.current;
      if (!scroller || !scroller.isConnected) {
        setVisible(false);
        return;
      }
      const box = scroller.getBoundingClientRect();
      // Off screen (e.g. a tab swiped away) or no longer scrolled.
      const onScreen =
        box.width > 0 &&
        box.left > -4 &&
        box.right < window.innerWidth + 4 &&
        box.height > 0;
      const scrolled = scroller.scrollTop > 2;
      if (onScreen && scrolled) {
        setRect({ top: box.top, left: box.left, width: box.width });
        setVisible(true);
      } else {
        setVisible(false);
      }
    };

    const onScroll = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.closest(OVERLAY_SELECTOR)) return;
      const isVertical =
        target.scrollHeight > target.clientHeight + 1 &&
        target.clientHeight > 160 &&
        target.clientWidth > window.innerWidth * 0.8;
      if (isVertical && (target.scrollTop > 0 || target === activeRef.current))
        activeRef.current = target;
      evaluate();
    };

    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", evaluate);
    return () => {
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", evaluate);
    };
  }, []);

  if (!rect) return null;
  return createPortal(
    <Shadow
      aria-hidden={true}
      $visible={visible}
      style={{ top: rect.top, left: rect.left, width: rect.width }}
    />,
    document.body
  );
};

export default PhoneChromeShadow;

const Shadow = styled.div<{ $visible: boolean }>`
  position: fixed;
  height: 12px;
  /* Above the page content, below drawers, modals and the bottom bar. */
  z-index: 20;
  pointer-events: none;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 150ms ease;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  background: ${(p) =>
    p.theme.isDarkMode
      ? "transparent"
      : "linear-gradient(to bottom, rgba(16, 24, 40, 0.06), rgba(16, 24, 40, 0))"};
`;
